import React, { useEffect, useMemo, useState } from 'react';
import { Eye, PencilSimple, Plus, GearSix, Trash } from '@phosphor-icons/react';
import PageHeader from '../../../shared/components/ui/PageHeader';
import GenericFilters from '../../../shared/components/ui/GenericFilters';
import GenericTables from '../../../shared/components/ui/GenericTables';
import ModalCreationEditing from '../../../shared/components/ui/ModalCreationEditing';
import ActionMenu from '../../../shared/components/ui/ActionMenu';
import { taskTypeService } from '../../../services/tasks/taskTypeService.js';
import { categoryService } from '../../../services/tasks/categoryService.js';
import { useGuidedTourStep } from '../../../shared/hooks/useGuidedTour.js';

const TaskTypeManagement = () => {
	const [taskTypes, setTaskTypes] = useState([]);
	const [categoryOptions, setCategoryOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [notification, setNotification] = useState({
		show: false,
		message: '',
		type: 'success',
		origin: '',
	});
	const [filters, setFilters] = useState({
		categoryName: '',
		displayName: '',
		isActive: 'active',
	});

	const [modalOpen, setModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState('create');
	const [selectedTaskType, setSelectedTaskType] = useState(null);

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [totalItems, setTotalItems] = useState(0);
	const [totalPages, setTotalPages] = useState(1);

	useEffect(() => {
		fetchTaskTypes();
	}, [filters.categoryName, filters.displayName, filters.isActive, currentPage, pageSize]);

	useEffect(() => {
		fetchCategories();
	}, []);

	useEffect(() => {
		if (!notification.show) return undefined;

		const timeoutId = setTimeout(() => {
			setNotification({ show: false, message: '', type: 'success', origin: '' });
		}, 5000);

		return () => clearTimeout(timeoutId);
	}, [notification.show]);

	const fetchTaskTypes = async () => {
		try {
			setLoading(true);
			setErrorMessage('');
			const params = {
				...filters,
				pageSize,
				pageCurrent: currentPage - 1,
			};
			const response = await taskTypeService.getTaskTypes(params);
			const data = response.taskTypes;
			setTaskTypes(data);

			if (response.pagination) {
				const responseTotalItems = Number(response.pagination.totalItems) || 0;
				const responsePageSize = Number(response.pagination.pageSize) || pageSize;

				setTotalItems(responseTotalItems);
				setTotalPages(responseTotalItems > 0 ? Math.ceil(responseTotalItems / responsePageSize) : 1);
			} else {
				setTotalItems(data.length);
				setTotalPages(1);
			}
		} catch (error) {
			console.error('Error loading task types:', error);
			setErrorMessage('No se pudieron cargar los tipos de tarea.');
		} finally {
			setLoading(false);
		}
	};

	const fetchCategories = async () => {
		try {
			const response = await categoryService.getCategories();
			const options = response.categories.map((category) => ({
				value: category.name,
				label: category.displayName,
			}));
			setCategoryOptions(options);
		} catch (error) {
			console.error('Error loading categories:', error);
		}
	};

	const handlePageChange = (newPage) => {
		setCurrentPage(newPage);
	};

	const handlePageSizeChange = (newPageSize) => {
		setPageSize(newPageSize);
		setCurrentPage(1);
	};

	const openCreateModal = () => {
		setSelectedTaskType(null);
		setModalMode('create');
		setModalOpen(true);
		setNotification({ show: false, message: '', type: 'success', origin: '' });
	};

	const openViewModal = (taskType) => {
		setSelectedTaskType(taskType);
		setModalMode('view');
		setModalOpen(true);
		setNotification({ show: false, message: '', type: 'success', origin: '' });
	};

	const openEditModal = (taskType) => {
		setSelectedTaskType(taskType);
		setModalMode('edit');
		setModalOpen(true);
		setNotification({ show: false, message: '', type: 'success', origin: '' });
	};

	const openDeleteModal = (taskType) => {
		setSelectedTaskType(taskType);
		setModalMode('delete');
		setModalOpen(true);
		setNotification({ show: false, message: '', type: 'success', origin: '' });
	};

	const closeModal = () => {
		if (isSubmitting) return;
		setModalOpen(false);
		setSelectedTaskType(null);
		setNotification((prev) =>
			prev.origin === 'task-types-modal' ? { show: false, message: '', type: 'success', origin: '' } : prev
		);
	};

	// Recorrido guiado (taskTypesTour.js): el paso con modal 'task-type-create' abre el alta y lo cierra al salir.
	const [tourStep, setTourStep] = useState(null);
	useGuidedTourStep((step) => {
		if (step?.modal === 'task-type-create' && tourStep?.modal !== 'task-type-create') openCreateModal();
		if (step?.modal !== 'task-type-create' && tourStep?.modal === 'task-type-create') closeModal();
		setTourStep(step);
	});

	const modalInitialValues = useMemo(
		() => ({
			taskTypeDisplayName: selectedTaskType?.displayName || '',
			categoryName: selectedTaskType?.categoryName || '',
			description: selectedTaskType?.description || '',
		}),
		[selectedTaskType]
	);

	const showNotification = (message, type = 'error', origin = 'task-types-modal') => {
		setNotification({
			show: true,
			message,
			type,
			origin,
		});
	};

	const handleSubmitTaskType = async (values) => {
		const displayName = values.taskTypeDisplayName?.trim() || '';
		const normalizedName = displayName.toLowerCase().replace(/\s+/g, '_');

		const payload = {
			name: normalizedName,
			categoryName: values.categoryName,
			description: values.description?.trim() || '',
			displayName: displayName,
		};

		if (!displayName) {
			showNotification('El nombre del tipo de tarea es obligatorio.', 'error', 'task-types-modal');
			return;
		}

		setIsSubmitting(true);
		setErrorMessage('');
		setNotification({ show: false, message: '', type: 'success', origin: '' });

		try {
			if (modalMode === 'create') {
				await taskTypeService.createTaskType(payload);
				showNotification('Tipo de tarea creado correctamente.', 'success', 'task-types-page');
			}

			if (modalMode === 'edit') {
				await taskTypeService.updateTaskType(selectedTaskType.name, payload);
				showNotification('Tipo de tarea actualizado correctamente.', 'success', 'task-types-page');
			}

			setModalOpen(false);
			setSelectedTaskType(null);
			await fetchTaskTypes();
		} catch (error) {
			console.error('Error saving task type:', error);
			showNotification(error.message || 'No se pudo guardar el tipo de tarea.', 'error', 'task-types-modal');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDeleteTaskType = async () => {
		if (!selectedTaskType?.name) {
			showNotification('No se pudo identificar el tipo de tarea a eliminar.', 'error', 'task-types-modal');
			return;
		}

		setIsSubmitting(true);
		setErrorMessage('');
		setNotification({ show: false, message: '', type: 'success', origin: '' });

		try {
			await taskTypeService.deleteTaskType(selectedTaskType.name);
			showNotification('Tipo de tarea eliminado correctamente.', 'success', 'task-types-page');
			setModalOpen(false);
			setSelectedTaskType(null);
			await fetchTaskTypes();
		} catch (error) {
			console.error('Error deleting task type:', error);
			showNotification(error.message || 'No se pudo eliminar el tipo de tarea.', 'error', 'task-types-modal');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleRestoreTaskType = async (taskType) => {
		if (!taskType?.name) {
			showNotification('No se pudo identificar el tipo de tarea a restaurar.', 'error', 'task-types-page');
			return;
		}

		setIsSubmitting(true);
		setErrorMessage('');

		try {
			await taskTypeService.restoreTaskType(taskType.name);
			showNotification('Tipo de tarea restaurado correctamente.', 'success', 'task-types-page');
			await fetchTaskTypes();
		} catch (error) {
			console.error('Error restoring task type:', error);
			showNotification(error.message || 'No se pudo restaurar el tipo de tarea.', 'error', 'task-types-page');
		} finally {
			setIsSubmitting(false);
		}
	};

	const filterColumns = [
		{
			type: 'select',
			key: 'categoryName',
			label: 'Categoría',
			options: [
				{ id: '', name: 'Todas' },
				...categoryOptions.map((option) => ({ id: option.value, name: option.label })),
			],
			optionValue: 'id',
			optionLabel: 'name',
		},
		{
			type: 'text',
			key: 'displayName',
			label: 'Nombre',
		},
		{
			type: 'select',
			key: 'isActive',
			label: 'Estado',
			options: [
				{ id: 'active', name: 'Activos' },
				{ id: 'inactive', name: 'Eliminadas' },
			],
		}
	];

	const modalFields = [
		{
			name: 'taskTypeDisplayName',
			tour: 'task-type-form-name',
			label: 'Nombre',
			type: 'text',
			required: true,
			maxLength: 100,
			placeholder: 'Ej. Instalacion domiciliaria',
			disabled: () => modalMode === 'view',
		},
		{
			name: 'categoryName',
			tour: 'task-type-form-category',
			label: 'Categoría',
			type: 'select',
			required: true,
			placeholder: 'Selecciona una categoría',
			options: categoryOptions,
			disabled: () => modalMode === 'view',
		},
		{
			name: 'description',
			tour: 'task-type-form-description',
			label: 'Descripcion del tipo de tarea',
			type: 'textarea',
			required: false,
			maxLength: 500,
			placeholder: 'Descripcion opcional',
			colSpan: 2,
			disabled: () => modalMode === 'view',
		},
	];

	const tableColumns = [
		{
			key: 'name',
			label: 'Nombre',
			align: 'center',
			render: (row) => row.displayName || '-',
		},
		{
			key: 'categoryName',
			label: 'Categoría',
			align: 'center',
			render: (row) => {
				const option = categoryOptions.find((o) => o.value === row.categoryName);
				return option ? option.label : (row.categoryName ? row.categoryName.charAt(0).toUpperCase() + row.categoryName.slice(1) : '-');
			},
		},
		{
			key: 'description',
			label: 'Descripción',
			align: 'center',
			render: (row) => {
				if (!row.description) return '-';
				return (
					<div className="max-w-xs truncate mx-auto" title={row.description}>
						{row.description}
					</div>
				);
			}
		},
		{
			key: 'actions',
			label: 'Acciones',
			align: 'center',
			isAction: true,
			render: (row) => (
				<span data-tour="task-type-row-actions" className="inline-flex">
				<ActionMenu
					options={
						filters.isActive === 'inactive'
							? [{ label: 'Restaurar', icon: <Plus size={16} />, onClick: () => handleRestoreTaskType(row) }]
							: [
								{ label: 'Ver', icon: <Eye size={16} />, onClick: () => openViewModal(row) },
								{ label: 'Editar', icon: <PencilSimple size={16} />, onClick: () => openEditModal(row) },
								{ label: 'Eliminar', icon: <Trash size={16} />, onClick: () => openDeleteModal(row) }
							]
					}
				/>
				</span>
			),
		},
	];

	return (
		<div className="space-y-6">
			<div data-tour="task-types-header">
			<PageHeader
				title="Tipo de Tareas"
				icon={<GearSix size={24} weight="duotone" />}
				subtitle="Administra los tipos de tarea y sus categorías"
				actions={(
					<button
						type="button"
						onClick={openCreateModal}
						className="bg-amber hover:bg-amber-hover text-on-amber px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-semibold transition-colors"
					>
						<Plus size={18} />
						Crear tipo de tarea
					</button>
				)}
			/>
			</div>

			{errorMessage && (
				<div className="rounded-lg border border-red-700 bg-red-900/20 p-3 text-sm text-red-300">{errorMessage}</div>
			)}

			{notification.show && notification.origin === 'task-types-page' && (
				<div
					className={`rounded-lg border p-3 text-sm ${
						notification.type === 'success'
							? 'border-green-700 bg-green-900/20 text-green-300'
							: 'border-red-700 bg-red-900/20 text-red-300'
					}`}
				>
					{notification.message}
				</div>
			)}

			<div data-tour="task-types-filters">
			<GenericFilters
				columns={filterColumns}
				filters={filters}
				onFilterChange={(nextFilters) => {
					setFilters(nextFilters);
					setCurrentPage(1);
				}}
				onClearFilters={() => {
					setFilters({ categoryName: '', displayName: '', isActive: 'active' });
					setCurrentPage(1);
				}}
				loading={loading}
			/>
			</div>

			<div data-tour="task-types-list" className="relative">
				<GenericTables
					columns={tableColumns}
					data={taskTypes}
					emptyMessage="No hay tipos de tarea disponibles"
					showFooter
					pagination={{
						currentPage,
						totalPages,
						pageSize,
						totalItems,
						pageSizeOptions: [5, 10, 20, 50],
						onPageChange: handlePageChange,
						onPageSizeChange: handlePageSizeChange,
					}}
				/>

				{loading && (
					<div className="absolute inset-0 flex items-center justify-center bg-bg/60 rounded-xl">
						<div className="flex items-center gap-3 rounded-lg border border-line bg-surface/90 px-4 py-3 text-sm text-content shadow-lg">
							<span className="h-5 w-5 animate-spin rounded-full border-2 border-amber border-t-transparent" />
							Cargando tipos de tarea...
						</div>
					</div>
				)}
			</div>

			<ModalCreationEditing
				isOpen={modalOpen}
				mode={modalMode === 'create' ? 'create' : modalMode === 'delete' ? 'delete' : 'edit'}
				icon={<GearSix size={22} weight="duotone" />}
				title={
					modalMode === 'delete'
						? 'Eliminar tipo de tarea'
						: modalMode === 'view'
							? 'Ver tipo de tarea'
							: modalMode === 'edit'
								? 'Editar tipo de tarea'
								: 'Crear tipo de tarea'
				}
				submitText={modalMode === 'edit' ? 'Guardar cambios' : 'Crear tipo de tarea'}
				fields={modalMode === 'delete' ? [] : modalFields}
				initialValues={modalInitialValues}
				notification={notification.origin === 'task-types-modal' ? notification : null}
				onSubmit={modalMode === 'delete' ? undefined : handleSubmitTaskType}
				onClose={closeModal}
				columns={2}
				isSubmitting={isSubmitting}
				actions={
					modalMode === 'delete' ? (
						<div className="rounded-lg border border-red-700 bg-red-900/20 p-4 text-red-200">
							¿Estás seguro de que quieres eliminar el tipo de tarea: <span className="font-semibold">{selectedTaskType?.displayName || '-'}</span>?
						</div>
					) : undefined
				}
				footer={
					modalMode === 'delete' ? (
						<div className="flex justify-end gap-4 pt-6 border-t border-line mt-6">
							<button
								type="button"
								onClick={closeModal}
								disabled={isSubmitting}
								className="px-6 py-3 bg-surface-2 text-content rounded-lg hover:bg-surface-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
							>
								Cancelar
							</button>
							<button
								type="button"
								onClick={handleDeleteTaskType}
								disabled={isSubmitting}
								className="px-6 py-3 bg-red-600 text-content rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
							>
								{isSubmitting ? 'Eliminando...' : 'Confirmar'}
							</button>
						</div>
					) : modalMode === 'view' ? (
						<div className="flex justify-end gap-4 pt-6 border-t border-line mt-6">
							<button
								type="button"
								onClick={closeModal}
								className="px-6 py-3 bg-surface-2 text-content rounded-lg hover:bg-surface-2 transition-colors"
							>
								Cerrar
							</button>
						</div>
					) : undefined
				}
			/>
		</div>
	);
};

export default TaskTypeManagement;
